import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Dictation wizard.
//
// This controller has TWO jobs, and only the first is the usual one.
//
// JOB 1 — repaint the frame-0 mirror. Frame 0 on this template is the PUZZLE
// BOARD: a complete, dense, self-explanatory task surface painted whole at
// opacity 1. What is withheld is not the screen, it is ten words' SPELLING.
// Every applier here therefore repaints something already visible. Four
// consequences, each a law rather than a style choice:
//
//   1. Nothing here touches `opacity`, ever. The reveal is a hard content swap
//      inside a fixed box plus a translate/scale settle at constant opacity;
//      there is no fade in this template and no fade-out at the end.
//   2. NOTHING HERE FILLS A SLOT. The board is empty at frame 0 and stays empty
//      until the reveal — a preview that painted a word into a slot would be
//      previewing the answer instead of the task, on the one template whose
//      entire pole argument is that the answer is what is withheld.
//   3. NO METER PILL IS FILLED. Pill k fills at play k's window start, so at
//      frame 0 all of them are outlined and none is accented. Lighting one here
//      would show a video that has already begun.
//   4. THE BOARD IS REBUILT, NOT RE-MEASURED IN TWO PLACES. The slot widths and
//      the fitted size are derived from ONE function below (`wordEm`), which is
//      a mirror of `Dictation::ConfigBuilder::CHAR_ADVANCE_EM` / `SLOT_PAD_EM` /
//      `SLOT_GAP_EM` and of `ShortsWizard::Dictation::Composition#word_em`. A
//      third independent derivation is exactly how a preview starts lying about
//      the render, so this file keeps the numbers in one place and names its
//      ancestor.
//
// JOB 2 — keep the content step's own counters honest while the author types.
// T6's structural laws are RAISES (2–4 groups, 5–16 words, ≤6 words per group,
// no underscores) and the fit contract is a per-GROUP width advisory, so this
// controller re-derives all four on every keystroke and surfaces them inline —
// before a save, rather than as a flash after one. It never renumbers by index
// in the input NAMES (they stay `groups[][text]`, the `[]` collection shape),
// because position in the document IS the binding.
//
// Unlike T5 this template HAS row CRUD, so rows are added and removed by the
// server actions rather than cloned here — a group is a free-standing layout
// row, not a positional binding to a parse of another field.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    // Job 1 — the preview panel.
    "frame", "flag", "languageLabel", "dictationBadge", "levelBadge", "wordCountPill",
    "replayMeter", "meterPill", "hookText", "glossBand", "board", "boardRow", "slot", "ctaText",
    // Job 2 — the content step's own DOM (the form side, not the preview).
    "groupRows", "groupRow", "groupInput", "groupMeta",
    "groupCounter", "wordCounter", "bandPill", "fitPill",
    "composedSentence", "boardSketch",
    "overlongWarning", "overlongList",
    "fitWarning", "unfitList", "fitPx",
    "underscoreWarning",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    sentence_gloss:       { apply: "applyGloss" },
    focus_note:           { apply: "applyFocusNote" },
    cta_text:             { apply: "applyCtaText" },
    group_text:           { apply: "applyGroups" },
    hint_level:           { apply: "applyGroups" },
    difficulty:           { apply: "applyDifficulty" },
    replay_count:         { apply: "applyReplayMeter" },
    target_language_code: { apply: "applyTargetLanguage" },
    learner_locale:       { apply: "applyLearnerLocale" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  // The gloss band's FRAME-0 occupant. `focus_note` takes the same band over at
  // the reveal and holds it to the last frame — same band, same height, zero
  // reflow of the board — so the note is stashed on the dataset rather than
  // painted here.
  applyGloss({ value }) { this.setText("glossBand", value) }

  applyFocusNote({ value }) {
    if (this.hasGlossBandTarget) this.glossBandTarget.dataset.focusNote = value || ""
  }

  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // The CEFR badge is painted LITERALLY — "B1" is locale-free, so unlike every
  // other chrome string it is never translated. It also re-checks the word band,
  // which is the ONLY thing `difficulty` changes: it is a badge and an advisory,
  // never a runtime dial.
  applyDifficulty({ value }) {
    this.setText("levelBadge", value)
    if (this.hasBandPillTarget) this.bandPillTarget.dataset.bandLabel = value || ""
    this.applyGroups()
  }

  // THE REPLAY METER — rebuilt as OUTLINED pills, never filled. It is the only
  // chrome in the fleet that says "this repeats" before it repeats, and its
  // frame-0 state is all pills painted and none accented.
  applyReplayMeter() {
    if (!this.hasReplayMeterTarget) return
    const count = parseInt(this.fieldValue("replay_count"), 10)
    if (!Number.isFinite(count) || count < 1) return

    const pills = this.meterPillTargets
    const template = pills[0]
    if (!template) return

    while (this.meterPillTargets.length > count) {
      this.meterPillTargets[this.meterPillTargets.length - 1].remove()
    }
    while (this.meterPillTargets.length < count) {
      const clone = template.cloneNode(true)
      this.replayMeterTarget.appendChild(clone)
    }
    this.meterPillTargets.forEach((pill, i) => { pill.textContent = String(i + 1) })
  }

  // The VOICED language of the sentence: the hero flag and the language label.
  // Base subtag throughout, so "en-GB" flags as 🇬🇧 — matching the builder's
  // resolved hero flag, which splits the subtag before the lookup.
  applyTargetLanguage({ value }) {
    this.setText("languageLabel", languageName(value, this.learnerLocale()))
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[baseSubtag(value)] || "🏳️"
  }

  // The READ language — the gloss, the listening note, the badge and the
  // word-count pill's label resolve in it. On this template it is also the
  // HOOK's spoken language: the one cross-language take in the video.
  applyLearnerLocale({ value }) {
    const locale = baseSubtag(value || "es")
    this.setText("dictationBadge", DICTATION_BADGE[locale] || DICTATION_BADGE.en)
    if (this.hasWordCountPillTarget) {
      this.wordCountPillTarget.dataset.wordsLabel = WORDS_LABEL[locale] || WORDS_LABEL.en
    }
    this.setText("languageLabel", languageName(this.fieldValue("target_language_code"), value))
    this.applyGroups()
  }

  // The palette drives every ink on the frame. The ACCENT carries four signals —
  // the reading band, the word at its fill frame, the filled meter pill and the
  // CTA — while the muted-ink ramp carries the filled/unfilled distinction. And
  // nothing here is ever danger-red: an empty slot is unfilled, not wrong.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- job 2 + the board -----------------------------------------------------

  // The whole parse, done once per keystroke: the words, the composed sentence,
  // the counters, the three warnings, the board sketch and the preview board.
  applyGroups() {
    const texts = this.groupTexts()
    const grouped = texts.map((text) => words(text))
    const total = grouped.reduce((n, list) => n + list.length, 0)
    const hint = this.fieldValue("hint_level") || "first_letter"

    this.setText("groupCounter", `${texts.length} ${plural(texts.length, "group")}`)
    this.setText("wordCounter", `${total} ${plural(total, "word")}`)
    this.toggleWarnClass("groupCounter", texts.length < MIN_GROUPS || texts.length > MAX_GROUPS)
    this.toggleWarnClass("wordCounter", total < MIN_WORDS || total > MAX_WORDS)

    this.updateBand(total)
    this.updateFit(grouped)
    this.updateOverlong(grouped)
    this.toggleWarn("underscoreWarning", texts.some((text) => UNDERSCORE_RE.test(text)))
    this.updateGroupMeta(texts, grouped)

    this.setText("composedSentence", composeSentence(texts))
    this.setText("boardSketch", grouped.map((list) => sketchRow(list, hint)).join("\n"))
    this.setText("wordCountPill", `${total} ${this.wordsLabel()}`)
    this.rebuildBoard(grouped, hint)
  }

  // The level word band (design §2 L-F) — advisory, never a raise, because the
  // level does not change behaviour.
  updateBand(total) {
    if (!this.hasBandPillTarget) return
    const level = this.bandPillTarget.dataset.bandLabel || ""
    const band = WORD_COUNT_BANDS[level]
    this.bandPillTarget.textContent = band ? `${level} band ${band[0]}–${band[1]}` : `${level} band`
    this.toggleWarnClass("bandPill", !!band && (total < band[0] || total > band[1]))
  }

  // The fit contract is a per-GROUP width advisory, not a word-count or
  // group-count one: the board is WIDTH-bound in every realistic case, so the
  // widest group decides the size of every slot on every row.
  updateFit(grouped) {
    const ems = grouped.map((list) => groupEm(list))
    const widest = ems.length ? Math.max(...ems) : 0
    const fitted = widest > 0 ? Math.min(BOARD_WIDTH_PX / widest, SLOT_MAX) : SLOT_MAX
    const rounded = Math.round(fitted)
    // Per GROUP, exactly as `Dictation::ConfigBuilder#warn_fit_advisories` does
    // it: a group's own width is what drives the fitted size below the floor.
    const unfit = ems
      .map((em, i) => (em > 0 && BOARD_WIDTH_PX / em < LEGIBILITY_FLOOR ? i + 1 : null))
      .filter(Boolean)

    this.setText("fitPill", `slots ≈ ${rounded}px`)
    this.toggleWarnClass("fitPill", unfit.length > 0)
    this.setText("unfitList", unfit.join(", "))
    this.setText("fitPx", String(rounded))
    this.toggleWarn("fitWarning", unfit.length > 0)
  }

  updateOverlong(grouped) {
    const over = grouped
      .map((list, i) => (list.length > MAX_WORDS_PER_GROUP ? i + 1 : null))
      .filter(Boolean)
    this.setText("overlongList", over.join(", "))
    this.toggleWarn("overlongWarning", over.length > 0)
  }

  updateGroupMeta(texts, grouped) {
    this.groupMetaTargets.forEach((el, i) => {
      const list = grouped[i] || []
      el.textContent = `${list.length}w · ${(texts[i] || "").length}c`
    })
  }

  // Rebuild the preview board from the authored rows. Slots are EMPTY (or carry
  // their hint letter) and pre-sized to their own word — the no-reflow law, and
  // the thing that buys the loop seam for free.
  rebuildBoard(grouped, hint) {
    if (!this.hasBoardTarget) return
    const rowTemplate = this.boardRowTargets[0]
    const slotTemplate = this.slotTargets[0]
    if (!rowTemplate || !slotTemplate) return

    const rows = grouped.map((list) => {
      const row = rowTemplate.cloneNode(false)
      list.forEach((word) => {
        const slot = slotTemplate.cloneNode(false)
        slot.style.width = `${wordEm(word).toFixed(2)}em`
        slot.textContent = hintLetter(word, hint) || " "
        row.appendChild(slot)
      })
      return row
    })

    this.boardTarget.replaceChildren(...rows)
    this.resizeBoard(grouped)
  }

  // The width-bound fit, applied to the board CONTAINER so every row and every
  // pre-sized slot inside it scales with the block's own em. That is what keeps
  // the no-reflow guarantee true at every font size.
  resizeBoard(grouped) {
    if (!this.hasBoardTarget) return
    const ems = grouped.map((list) => groupEm(list))
    const widest = ems.length ? Math.max(...ems) : 0
    const base = widest > 0
      ? clamp(PREVIEW_BOARD_WIDTH_PX / widest, PREVIEW_SLOT_MIN_PX, PREVIEW_SLOT_MAX_PX)
      : PREVIEW_SLOT_MAX_PX
    this.boardTarget.dataset.baseFontSize = String(Math.round(base))
    const scale = positiveOr(this.fieldValue("font_size_scale"), 1)
    this.boardTarget.style.fontSize = `${Math.round(base * scale)}px`
  }

  groupTexts() {
    return this.groupInputTargets.map((el) => el.value || "")
  }

  wordsLabel() {
    if (this.hasWordCountPillTarget) return this.wordCountPillTarget.dataset.wordsLabel || "words"
    return "words"
  }

  toggleWarn(targetName, show) {
    const has = `has${capitalize(targetName)}Target`
    if (!this[has]) return
    this[`${targetName}Target`].hidden = !show
  }

  toggleWarnClass(targetName, warn) {
    const has = `has${capitalize(targetName)}Target`
    if (!this[has]) return
    this[`${targetName}Target`].classList.toggle("dic-counter__pill--warn", warn)
  }

  // -- typography / font-size / stroke --------------------------------------

  // DISPLAY role: the hook and the board — the two surfaces that sit over a
  // background asset and the two the renderer strokes. BODY role: the gloss
  // band, the badges, the pills, the meter and the CTA, which sit on the theme
  // surface and skip the stroke. The HINT LETTERS skip too, and that is a rule
  // rather than an oversight: a stroked glyph inside a pre-sized slot would
  // visually overflow the box it must not resize, and it would make the letter
  // that was GIVEN read heavier than the word that was EARNED.
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    applyTypographyToElements(
      { display: this.displayTargets(), body: this.bodyTargets() },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    applyFontSizeScale(this.displayTargets().map((el) => sizedPair(el, 13)), displayScale)
    applyFontSizeScale(this.bodyTargets().map((el) => sizedPair(el, 9)), bodyScale)
  }

  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  learnerLocale() {
    return this.fieldValue("learner_locale") || "es"
  }

  displayTargets() {
    return [
      this.hasHookTextTarget ? this.hookTextTarget : null,
      this.hasBoardTarget ? this.boardTarget : null,
    ].filter(Boolean)
  }

  bodyTargets() {
    const singles = [ "dictationBadge", "levelBadge", "wordCountPill", "glossBand", "ctaText" ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
    return [ ...singles, ...this.meterPillTargets ]
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// ---------------------------------------------------------------------------
// THE PRE-SIZED SLOT, mirrored from `Dictation::ConfigBuilder::CHAR_ADVANCE_EM`
// / `SLOT_PAD_EM` / `SLOT_GAP_EM` / `MIN_SLOT_EM` / `MAX_SLOT_EM` — the same
// numbers `ShortsWizard::Dictation::Composition#word_em` reads and the same ones
// `computeDictationSizing` uses in the renderer. This is the THIRD leg of that
// mirror and the only one that cannot import the constant, so it names its
// ancestor instead: change the builder and change this together.
const CHAR_ADVANCE_EM = 0.52
const SLOT_PAD_EM = 0.50
const SLOT_GAP_EM = 0.42
const MIN_SLOT_EM = 1.4
const MAX_SLOT_EM = 9.0

// The renderer's usable board width and its two-axis clamp (design §3).
const BOARD_WIDTH_PX = 880
const LEGIBILITY_FLOOR = 34
const SLOT_MAX = 84

// 880 renderer px of usable board width ≈ 300 preview px at the panel's frame
// size, so the renderer's 34..84 band maps to roughly 12..28 here. Mirrors
// ShortsWizard::Dictation::LivePreviewComponent's PREVIEW_* constants.
const PREVIEW_BOARD_WIDTH_PX = 300
const PREVIEW_SLOT_MAX_PX = 28
const PREVIEW_SLOT_MIN_PX = 12

// The structural bounds, mirrored from the builder (design §2 laws L-A / L-D).
// Every one of these is a RAISE at build time, which is exactly why they are
// surfaced inline while the author types.
const MIN_GROUPS = 2
const MAX_GROUPS = 4
const MIN_WORDS = 5
const MAX_WORDS = 16
const MAX_WORDS_PER_GROUP = 6

// Any underscore at all is refused: the slots are DERIVED from the words, never
// authored, and `_` is a censor character on a surface that gets spoken.
const UNDERSCORE_RE = /_/

// The advisory word band per CEFR level (law L-F). `difficulty` paints a badge
// and gates this band — it is never a runtime dial.
const WORD_COUNT_BANDS = {
  "A1": [ 5, 8 ],
  "A2": [ 7, 10 ],
  "B1": [ 9, 13 ],
  "B2": [ 12, 16 ],
  "C1": [ 12, 16 ],
  "C2": [ 12, 16 ],
}

// A GROUP'S WORDS ARE ITS WHITESPACE TOKENS, punctuation included (law L-C).
// No tokenizer, no punctuation stripping — the board is a typewriter view of the
// sentence and the reveal has to give it back character for character.
function words(text) {
  return (text || "").toString().split(/\s+/).filter((w) => w.length > 0)
}

// VERBATIM CONCATENATION (law L-B): the groups joined with a single space, one
// whitespace collapse, no other join heuristics.
function composeSentence(texts) {
  return texts
    .map((text) => (text || "").trim())
    .filter((text) => text.length > 0)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()
}

function wordEm(word) {
  return clamp((word || "").length * CHAR_ADVANCE_EM + SLOT_PAD_EM, MIN_SLOT_EM, MAX_SLOT_EM)
}

function groupEm(list) {
  if (!list.length) return 0
  return list.reduce((total, word) => total + wordEm(word), 0) + (list.length - 1) * SLOT_GAP_EM
}

// A leading punctuation mark counts as the first character and its letter comes
// with it (`¿Qué` → `¿Q`), so a one-character word is fully painted — honest,
// unavoidable, and never the lesson.
function hintLetter(word, hint) {
  if (hint !== "first_letter") return ""
  return (word || "").slice(0, 1)
}

function sketchRow(list, hint) {
  return list
    .map((word) => {
      const lead = hintLetter(word, hint)
      return lead + "_".repeat(Math.max(word.length - lead.length, 1))
    })
    .join(" ")
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function plural(count, word) {
  return count === 1 ? word : `${word}s`
}

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// "en-GB" → "en". The flag and name lookups are keyed on the base subtag.
function baseSubtag(code) {
  return (code || "").toString().split("-")[0].toLowerCase()
}

// The label is localized in the VIEWER's language, mirroring
// LanguageNameResolver.call(code, locale: learner_locale) on the server — a Modo
// video (viewer L1 = es) reads "Inglés", not "English".
function languageName(code, learnerLocale) {
  const base = baseSubtag(code)
  const table = LANGUAGE_NAMES[baseSubtag(learnerLocale)] || LANGUAGE_NAMES.en
  return table[base] || LANGUAGE_NAMES.en[base] || base.toUpperCase()
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) are never drawn. On T6 they
// are legal ONLY in the hook — the builder RAISES on a tag inside a group,
// because a tag is a take boundary and this sentence is ONE take — so stripping
// is for the hook's sake and so a hand-edited payload still draws clean.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

// All seven language-learning looks, including the two channel-locked ones — a
// channel-bound config CAN carry `cafecito` / `modo`, and repainting it as
// notebook would be the preview lying about the render.
const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
  "cafecito":   { "bg": "#F6EDDF", "surface": "#FFFBF2", "text": "#3B2A1E", "muted": "#6E5A47", "accent": "#C98A2D" },
  "modo":       { "bg": "#1E2733", "surface": "#28323F", "text": "#F2F5F9", "muted": "#A8B4C2", "accent": "#FFB020" },
}

// The franchise pill and the word-count pill's label, mirroring
// `niche_templates.dictation.*` in the three shipped locale files.
const DICTATION_BADGE = {
  "en": "WRITE IT DOWN",
  "es": "ESCRÍBELO",
}

const WORDS_LABEL = {
  "en": "words",
  "es": "palabras",
}

// Mirrors config/locales/language_names.{en,es}.yml for the languages the
// content step offers. An unknown pair falls back to the English name, then to
// the upper-cased code — the same degrade the server's `default:` gives.
const LANGUAGE_NAMES = {
  en: {
    "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
    "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
    "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
    "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
  },
  es: {
    "ar": "Árabe", "de": "Alemán", "en": "Inglés", "es": "Español", "fr": "Francés",
    "hi": "Hindi", "it": "Italiano", "ja": "Japonés", "ko": "Coreano", "nl": "Neerlandés",
    "pl": "Polaco", "pt": "Portugués", "ru": "Ruso", "sv": "Sueco", "th": "Tailandés",
    "tr": "Turco", "vi": "Vietnamita", "zh": "Chino",
  },
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
